import React from 'react';

const buildColumns = (data, globalExtraFields = new Set()) => {
  if (!Array.isArray(data) || data.length === 0) return [];
  const columns = [];
  const seen = new Set();

  const firstRow = data[0] || {};
  Object.keys(firstRow).forEach(key => {
    if (!seen.has(key)) {
      seen.add(key);
      columns.push(key);
    }
  });

  data.slice(1).forEach(row => {
    Object.keys(row || {}).forEach(key => {
      if (!seen.has(key)) {
        seen.add(key);
        columns.push(key);
      }
    });
  });

  globalExtraFields.forEach(key => {
    if (!seen.has(key)) {
      seen.add(key);
      columns.push(key);
    }
  });

  if (columns.includes('row')) {
    columns.splice(columns.indexOf('row'), 1);
    columns.unshift('row');
  }

  return columns;
};

const buildErrorMap = errors => {
  const errorMap = new Map();
  const globalExtraFields = new Set();

  (errors || []).forEach(err => {
    const rowNumber = err && err.row !== undefined ? err.row : null;
    let fields = err && err.fields !== undefined ? err.fields : [];
    let fieldList = [];

    if (Array.isArray(fields)) {
      fieldList = fields;
    } else if (typeof fields === 'string') {
      fieldList = fields
        .split(',')
        .map(field => field.trim())
        .filter(Boolean);
    }

    let extraFields = err && err.extraFields !== undefined ? err.extraFields : [];
    let extraFieldList = [];

    if (Array.isArray(extraFields)) {
      extraFieldList = extraFields;
    } else if (typeof extraFields === 'string') {
      extraFieldList = extraFields
        .split(',')
        .map(field => field.trim())
        .filter(Boolean);
    }

    if (rowNumber === null) return;

    if (fieldList.length > 0) {
      const current = errorMap.get(rowNumber) || new Set();
      fieldList.forEach(field => current.add(field));
      errorMap.set(rowNumber, current);
    }

    if (extraFieldList.length > 0) {
      extraFieldList.forEach(field => {
        globalExtraFields.add(field);
      });
    }
  });

  return { errorMap, globalExtraFields };
};

const PreviewTable = ({ data, errors, message, success }) => {
  const { errorMap, globalExtraFields } = buildErrorMap(errors);
  const columns = buildColumns(data, globalExtraFields);
  const tableKey = `${columns.join('|')}-${data.length}-${success}`;

  if (!message && (!Array.isArray(data) || data.length === 0)) return null;

  return (
    <div className="mt-3">
      <style>{`
        .preview-table-scroll { overflow: auto; max-height: 300px; scrollbar-width: thin; }
        .preview-table-scroll::-webkit-scrollbar { height: 12px; }
        .preview-table-scroll::-webkit-scrollbar-thumb { background: #b5b5b5; border-radius: 6px; }
        .preview-table thead th {
          position: sticky;
          top: -1px;
          background: #f8f9fa;
          z-index: 3;
        }
        .preview-table th:first-child,
        .preview-table td:first-child {
          position: sticky;
          left: -1px;
          background: #f8f9fa;
          z-index: 2;
        }
        .preview-table thead th:first-child { z-index: 4; }
      `}</style>

      {message && (
        <div
          className={`alert ${success ? 'alert-success' : 'alert-danger'}`}
          role="alert"
        >
          {message}
        </div>
      )}

      {Array.isArray(data) && data.length > 0 && (
        <div className="table-responsive preview-table-scroll">
          <table className="table table-bordered table-sm preview-table" key={tableKey}>
            <thead className="thead-light">
              <tr>
                {columns.map((column, columnIndex) => (
                  <th
                    key={`${column}-${columnIndex}`}
                    style={globalExtraFields.has(column) ? { backgroundColor: '#ffc7cc', color: 'red' } : undefined}
                  >
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((row, rowIndex) => {
                const rowNumber = row && row.row !== undefined ? row.row : rowIndex + 1;
                const rowErrors = errorMap.get(rowNumber) || new Set();
                return (
                  <tr key={`${rowNumber}-${rowIndex}`}>
                    {columns.map((column, columnIndex) => {
                      const isError = rowErrors.has(column);
                      const isExtra = globalExtraFields.has(column);
                      let cellStyle = undefined;

                      if (isExtra || isError) {
                        cellStyle = { backgroundColor: '#f8d7da' };
                      }

                      const value = row && row[column] !== undefined && row[column] !== null ? row[column] : '';
                      return (
                        <td
                          key={`${rowNumber}-${column}-${columnIndex}`}
                          style={cellStyle}
                        >
                          {String(value)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PreviewTable;
